import { useState, useEffect, useRef, useMemo } from "react";
import { useSnackbar } from "notistack";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import axios, { axiosPrivate } from "../../../api/axios";
import {
  useParams,
  useNavigate,
  useLocation,
  Link
} from "react-router-dom";

import { EventTabs } from "../../../utils/constants";

import JoditEditor from "jodit-react";

import { DateTime } from 'luxon';
import {
  AdapterLuxonSunday
} from "../../../utils/AdapterSundayLuxon";

import {
  LocalizationProvider
} from "@mui/x-date-pickers/LocalizationProvider";

import {
  DatePicker
} from "@mui/x-date-pickers/DatePicker";

import {
  DateTimePicker
} from "@mui/x-date-pickers/DateTimePicker";

import styles from "./EventsEditForm.module.css";


const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#0074c8"
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#0074c8"
  }
};


// ---------------------------------------------------------
// DEFAULT EVENT
// ---------------------------------------------------------

const defaultEvent = {
  EventsID: 0,

  BP_Title: "",
  BP_Type: "",
  BP_Partnership: "",
  BP_AccountingCode: "",

  BP_Categories: [],

  BP_Address: "",

  BP_StartDate: "",
  BP_EndDate: "",

  BP_ShowDateOnly: true,
  BP_ShowEndTime: false,
  BP_DatesFlexible: false,

  BP_Overnight: "",
  BP_Gender: "",

  BP_AgeGradeRestrictions: "",
  BP_AgeGrade: "",

  BP_Description: "",

  BP_NCBMManaged: false,

  BP_ContactName: "",
  BP_ContactEmail: "",
  BP_SendContactAdminEmail: false,
  BP_ContactPhone: "",

  BP_Coordinator: 0,
  BP_ShowCoordinatorPhone: false,
  BP_ShowCoordinatorEmail: false,
  BP_SendCoordinatorEmail: false,

  BP_Administrator: 0,
  BP_ShowAdministratorPhone: false,
  BP_ShowAdministratorEmail: false,
  BP_SendAdministratorEmail: false,

  BP_ButtonText: "",

  BP_ThankYouMessage: `
<p>Dear Volunteer,</p>

<p>
Thank you for registering the following attendee(s) for
<strong>
<a href="{%Domain%}/events/details/{%EventID%}">
{%EventName%}
</a>
</strong>.
</p>

<p>{%Attendees%}</p>

<p>
<strong>Next Step:</strong>
Visit
<a href="{%Domain%}/myreserved">
My Reservations &amp; Payments
</a>
in the volunteer portal to continue registration by making payment for the
event/project.
</p>
`,

  BP_PendingApprovalMessage: `
<p>Dear Volunteer,</p>

<p>
Your information has been submitted to the event coordinator of
<strong>{%EventName%}</strong>
to be approved.
</p>

<p>
If approved, you'll receive an email with instructions to complete your
registration.
</p>
`,

  BP_ApprovalMessage: `
<p>Dear Volunteer,</p>

<p>
You have been approved to attend
<strong>{%EventName%}</strong>
as a(n)
<strong>{%Type%}</strong>.
</p>

<p>
<strong>Next Step:</strong>
Visit <a href="{%Domain%}/myreserved">My Reservations &amp; Payments</a> in the volunteer portal to continue
registration by making payment for the event/project.
</p>
`,

  BP_DeniedApprovalMessage: `
<p>Dear Volunteer,</p>

<p>
Unfortunately, you have not been approved to attend
<strong>{%EventName%}</strong>
as a(n)
<strong>{%Type%}</strong>.
</p>

<p>
<strong>What do I do now?</strong>
Please contact us to discuss what volunteer opportunities we can still
offer you.
</p>
`,

  BP_BGCheckMessage: `
<p>
Thank you for registering for
<strong>{%EventName%}</strong>.
As I’m sure you understand, our policy is that anyone age 18 and above who
serves with children and youth must submit to a professional background
check. The safety and well-being of the children is of primary importance.
Thank you for your cooperation in this.
</p>

<p>
Please click on the following link in order to begin the process of
completing your background check:
</p>

<p class="center-block center">
<a
  href="https://ciaresearch.com/ec=NCBaptist"
  target="_blank"
  rel="noreferrer"
>
Submit Background Check
</a>
</p>

<p>
Your information will be reviewed by Protect My Ministry and results will
be sent to us; this will take a few days. If any issues come up regarding
your check, we will be in touch with you. Otherwise, you will receive an
e-mail stating that your registration has been confirmed.
</p>
`,
BP_AdditionalMessage: `
<p>Your registration has been updated successfully!</p>
<p>The additional team member(s) have been added to your existing registration. A confirmation email has been sent to your inbox.
You can review your registration and complete any remaining payment from My Reservations & Payments, or by mail or phone.</p>
<p>The following team member(s) have been added to your registration for <strong>
<a href="{%Domain%}/events/details/{%EventID%}">
{%EventName%}
</a>
</strong>:</p>
<p>{%Attendees%}</p>
<p><strong>Next Step:</strong> Visit <a href="{%Domain%}/myreserved">
My Reservations & Payments
</a> in the volunteer portal to review your updated registration or make any remaining payment for the event/project.</p>
`,

  BP_RequiredShots: [],

  BP_Private: false,
  BP_PrivateURL: "",

  BP_RequireCheckin: false,
  BP_RequireCheckinPhoto: false
};


// ---------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------

const EventsForm = () => {
  
  const editor = useRef(null);
  
    const config = useMemo(
      () => ({
        readonly: false, // Set to true to make the editor read-only
      placeholder: 'Start typing your content here...',
      height: 400,
      toolbarButtonSize: 'middle',
      theme: 'default', // Options: 'default' or 'dark'
      enableDragAndDropFileToEditor: true,
      
      // Customize which toolbar buttons appear
      buttons: [
        'bold', 'italic', 'underline', 'strikethrough', '|',
        'ul', 'ol', '|',
        'font', 'fontsize', 'paragraph', '|',
        'image', 'table', 'link', '|',
        'align', 'undo', 'redo', '|',
        'hr', 'eraser', 'fullsize', 'source'
      ],
      
      // Configure file uploads
      uploader: {
        url: '/api/files/upload',
        format: 'json',
        // Define a custom send function to bypass Jodit's built-in fetch/XHR
      send: async (data, success, failure) => {
        try {
          //console.log('reached the send method');
          // extract the actual File out of Jodit's FormData and re-key it
    const fd = new FormData();
    for (const [, value] of data.entries()) {
      if (value instanceof File) fd.append('file', value);
    }
    const response = await axiosPrivate.post('/api/files/upload', fd);
    success(response.data);
        } catch (error) {
          // Pass the error message back to Jodit's UI failure handler
          failure(error.response?.data?.message || error.message || 'Upload failed');
        }
      },
        // Tell Jodit if the request succeeded based on your API signature
    //isSuccess: (resp) => resp.data.success === true,
    // Remap your custom fields into the fields Jodit requires
   process: (resp) => ({
    files: [resp?.data?.url],
    baseurl: '',
    error: resp?.success ? null : 'Upload failed',
    }),
        insertImageAsBase64URI: true // Quickly inserts images directly as base64
      },
      
      // Hide the bottom status bar statistics if desired
      showXPathInStatusbar: false,
      showCharsCounter: true,
      showWordsCounter: true,
      }),
      []
    );

  const params = useParams();

  const eventid = params.eventid;

  const isEditMode = Boolean(eventid);

  const navigate = useNavigate();

  const location = useLocation();

  const {
    enqueueSnackbar
  } = useSnackbar();


  // -------------------------------------------------------
  // STATE
  // -------------------------------------------------------

  const [bomEvent, setFields] = useState(defaultEvent);

  const [categoriesList, setCategoriesList] = useState([]);

  const [partnershipsList, setPartnershipsList] = useState([]);

  const [accountingCodesList, setAccountingCodesList] = useState([]);

  const [eventCoordinators, setEventCoordinators] = useState([]);

  const [immunizations, setImmunizations] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const [isSaving, setIsSaving] = useState(false);

  const [error, setError] = useState(false);


  // -------------------------------------------------------
  // ACTIVE TAB
  // -------------------------------------------------------

  const urlSegment =
    location.pathname.split("/")[3]?.toLowerCase() || "general";

  const activeTabName =
    (
      EventTabs.find(
        t => t.path.toLowerCase() === urlSegment
      ) ||
      EventTabs[0]
    ).name;


  // -------------------------------------------------------
  // SUPPORTING LISTS
  // -------------------------------------------------------

  useEffect(() => {

    const controller = new AbortController();

    loadFormData(controller.signal);

    return () => {
      controller.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventid]);


  async function loadFormData(signal) {

    setIsLoading(true);
    setError(false);

    const endpoints = [
      "/api/basic/CMS_Category",
      "/api/events/projectgroups",
      "/api/events/projectgroups",
      "/api/users/role/18",
      "/api/immunizations"
    ];

    if (isEditMode) {
      endpoints.push(`/api/events/${eventid}`);
    }


    try {

      const results = await Promise.allSettled(

        endpoints.map(endpoint =>
          axios.get(endpoint, {
            headers: {
              "Content-Type": "application/json"
            },
            signal
          })
        )

      );


      if (signal?.aborted) {
        return;
      }


      const getResult = index => {

        return results[index].status === "fulfilled"
          ? results[index].value.data
          : null;

      };


      const categories = getResult(0) || [];

      const partnerships = getResult(1) || [];

      const accountingCodes = getResult(2) || [];

      const coordinators = getResult(3) || [];

      const immunizationsList = getResult(4) || [];


      setCategoriesList(categories);

      setPartnershipsList(partnerships);

      setAccountingCodesList(accountingCodes);

      setEventCoordinators(coordinators);

      setImmunizations(immunizationsList);


      // ---------------------------------------------------
      // CREATE MODE
      // ---------------------------------------------------

      if (!isEditMode) {

        setFields({
          ...defaultEvent
        });

        setIsLoading(false);

        return;
      }


      // ---------------------------------------------------
      // EDIT MODE
      // ---------------------------------------------------

      const eventData = getResult(5);


      if (!eventData) {

        setError(true);

        enqueueSnackbar(
          "Failed to load event data",
          {
            variant: "error"
          }
        );

        setIsLoading(false);

        return;
      }


      const normalizedEvent = {
        ...defaultEvent,
        ...eventData
      };


      // ---------------------------------------------------
      // DATE NORMALIZATION
      // ---------------------------------------------------

      try {

        if (normalizedEvent.BP_StartDate) {

          normalizedEvent.BP_StartDate =
            DateTime.fromISO(
              normalizedEvent.BP_StartDate
            );

        }

        if (normalizedEvent.BP_EndDate) {

          normalizedEvent.BP_EndDate =
            DateTime.fromISO(
              normalizedEvent.BP_EndDate
            );

        }

      } catch (dateError) {

        console.error(
          "Date Parse Issue",
          dateError
        );

      }


      // ---------------------------------------------------
      // CATEGORY / IMMUNIZATION NORMALIZATION
      // ---------------------------------------------------

      normalizedEvent.BP_Categories =
        stringToArray(
          normalizedEvent.BP_Categories
        );


      normalizedEvent.BP_RequiredShots =
        stringToArray(
          normalizedEvent.BP_RequiredShots
        );


      setFields(normalizedEvent);

      setIsLoading(false);

    } catch (err) {

      if (signal?.aborted) {
        return;
      }

      console.error(
        "Error loading event form:",
        err
      );

      setError(true);

      enqueueSnackbar(
        "Failed to load event data",
        {
          variant: "error"
        }
      );

      setIsLoading(false);
    }
  }


  // -------------------------------------------------------
  // STRING -> ARRAY
  // -------------------------------------------------------

  function stringToArray(value) {

    if (Array.isArray(value)) {
      return value;
    }

    if (!value) {
      return [];
    }

    return value
      .split(";")
      .map(item => parseInt(item, 10))
      .filter(item => !Number.isNaN(item));

  }


  // -------------------------------------------------------
  // FIELD HANDLERS
  // -------------------------------------------------------

  const handleFieldChange = input => {

    const {
      id,
      name,
      value
    } = input.target;

    const fieldName =
      id || name;

    setFields(prev => ({
      ...prev,
      [fieldName]: value
    }));

  };


  const handleCheckboxChange = input => {

    const {
      id: name,
      type,
      checked,
      value
    } = input.target;

    setFields(prev => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value
    }));

  };


  const handleCategoriesChange = e => {

    setFields(prev => ({
      ...prev,
      BP_Categories:
        e.target.value
    }));

  };


  const handlePartnershipsChange = e => {

    setFields(prev => ({
      ...prev,
      BP_Partnership:
        e.target.value
    }));

  };


  const handleAccountingCodesChange = e => {

    setFields(prev => ({
      ...prev,
      BP_AccountingCode:
        e.target.value
    }));

  };


  const handleCoordinatorChange = e => {

    setFields(prev => ({
      ...prev,

      BP_Coordinator:
        e.target.value === ""
          ? null
          : Number(e.target.value)
    }));

  };


  const handleAdministratorChange = e => {

    setFields(prev => ({
      ...prev,

      BP_Administrator:
        e.target.value === ""
          ? null
          : Number(e.target.value)
    }));

  };


  const handleImmunizationsChange = e => {

    setFields(prev => ({
      ...prev,
      BP_RequiredShots:
        e.target.value
    }));

  };


  const handleTypeChange = e => {

    setFields(prev => ({
      ...prev,
      BP_Type:
        e.target.value
    }));

  };


  const handleOvernightChange = e => {

    setFields(prev => ({
      ...prev,
      BP_Overnight:
        e.target.value
    }));

  };


  const handleGenderChange = e => {

    setFields(prev => ({
      ...prev,
      BP_Gender:
        e.target.value
    }));

  };


  const handleStartDateChange = value => {

    setFields(prev => ({
      ...prev,
      BP_StartDate:
        value
    }));

  };


  const handleEndDateChange = value => {

    setFields(prev => ({
      ...prev,
      BP_EndDate:
        value
    }));

  };

   //handle tab change
  const handleTabChange = (_, newValue) => {
  const tab = EventTabs.find(t => t.name === newValue);

  if (!tab || !eventid) {
    return;
  }

  navigate(`/admin/events/${tab.path}/${eventid}`);
};


  const handleEditorChange = content => {

    setFields(prev => ({
      ...prev,
      BP_Description:
        content
    }));

  };


  // -------------------------------------------------------
  // CANCEL
  // -------------------------------------------------------

  const handleCancel = () => {

    if (isEditMode) {

      navigate(
        `/admin/events/general/${eventid}`
      );

    } else {

      navigate("/admin/events");

    }

  };


  // -------------------------------------------------------
  // SAVE
  // -------------------------------------------------------

  const handleSubmit = async e => {

    e.preventDefault();

    if (isSaving) {
      return;
    }

    setIsSaving(true);


    try {

      const newEvent = {
        ...bomEvent
      };


      // ---------------------------------------------------
      // NORMALIZE CATEGORIES
      // ---------------------------------------------------

      newEvent.BP_Categories =
        Array.isArray(newEvent.BP_Categories)
          ? newEvent.BP_Categories.join(";")
          : newEvent.BP_Categories || "";


      // ---------------------------------------------------
      // NORMALIZE IMMUNIZATIONS
      // ---------------------------------------------------

      newEvent.BP_RequiredShots =
        Array.isArray(newEvent.BP_RequiredShots)
          ? newEvent.BP_RequiredShots.join(";")
          : newEvent.BP_RequiredShots || "";


      // ---------------------------------------------------
      // PUBLISH DATES
      // ---------------------------------------------------

      newEvent.BP_PublishFromDate =
        newEvent.BP_StartDate;

      newEvent.BP_PublishToDate =
        newEvent.BP_EndDate;




      // ---------------------------------------------------
      // EDIT
      // ---------------------------------------------------

      if (isEditMode) {

        const response =
          await axios.put(
            `/api/events/${eventid}`,
            newEvent
          );



        enqueueSnackbar(
          "Event Updated",
          {
            variant: "success"
          }
        );


        // Stay on current tab
        return;
      }

     


      // ---------------------------------------------------
      // CREATE
      // ---------------------------------------------------

      const {
        EventsID,
        ...createPayload
      } = newEvent;


      const response =
        await axios.post(
          "/api/events",
          createPayload
        );




      const newEventID =
        response.data?.EventsID ||
        response.data?.event?.EventsID ||
        response.data?.id;


      if (!newEventID) {

        throw new Error(
          "Event was created but the API did not return the new event ID."
        );

      }


      enqueueSnackbar(
        "Event Created",
        {
          variant: "success"
        }
      );


      // ---------------------------------------------------
      // IMPORTANT
      // Redirect into the normal event edit flow.
      // This causes the newly created event to have its tabs.
      // ---------------------------------------------------

      navigate(
        `/admin/events/general/${newEventID}`
      );

    } catch (err) {

      console.error(
        "Failed to save event:",
        err
      );


      enqueueSnackbar(
        `Failed to ${
          isEditMode
            ? "update"
            : "create"
        } event: ${
          err?.response?.data?.error ||
          err?.message ||
          "Unknown error"
        }`,
        {
          variant: "error"
        }
      );

    } finally {

      setIsSaving(false);

    }

  };


  // -------------------------------------------------------
  // LOADING
  // -------------------------------------------------------

  if (isLoading) {

    return (

      <main>

        <div className="full-row light-blue">

          <div className="container-fluid">

            <div className="row text-white pt-3 pb-3">

              <div className="col-md-12 text-white">

                <h1 className="text-uppercase">

                  {isEditMode
                    ? "Edit Event"
                    : "Create Event"}

                </h1>

                <p>

                  {isEditMode
                    ? "Loading event details..."
                    : "Preparing event form..."}

                </p>

              </div>

            </div>

          </div>

        </div>


        <div className="full-row white">

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 8
            }}
          >

            <Typography>

              Loading...

            </Typography>

          </Box>

        </div>

      </main>

    );

  }


  // -------------------------------------------------------
  // ERROR
  // -------------------------------------------------------

  if (error) {

    return (

      <main>

        <div className="full-row light-blue">

          <div className="container-fluid">

            <div className="row text-white pt-3 pb-3">

              <div className="col-md-12 text-white">

                <h1 className="text-uppercase">

                  Edit Event

                </h1>

              </div>

            </div>

          </div>

        </div>


        <div className="full-row white">

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 8
            }}
          >

            <Typography color="error">

              Unable to load this event.

            </Typography>

          </Box>

        </div>

      </main>

    );

  }


  // -------------------------------------------------------
  // RENDER
  // -------------------------------------------------------

  return (

    <main>

      {/* --------------------------------------------- */}
      {/* HEADER */}
      {/* --------------------------------------------- */}

      <div className="full-row light-blue">

        <div className="container-fluid">

          <div className="row text-white pt-3 pb-3">

            <div className="col-md-12 text-white">

              <h1 className="text-uppercase">

                {isEditMode
                  ? `Edit: ${bomEvent.BP_Title || "Event"}`
                  : "Create Event"}

              </h1>

              <p>

                {isEditMode
                  ? "Manage event details"
                  : "Add a new event"}

              </p>

            </div>

          </div>

        </div>

      </div>


      {/* --------------------------------------------- */}
      {/* CONTENT */}
      {/* --------------------------------------------- */}

      <div className="full-row white">


        {/* ------------------------------------------- */}
        {/* TAB BAR */}
        {/* ------------------------------------------- */}

        <Box
          sx={{
            position: "sticky",
            top: 40,
            zIndex: 100,
            backgroundColor: "white",
            borderBottom: 1,
            borderColor: "divider",
            boxShadow:
              "0 2px 8px rgba(0,0,0,0.04)"
          }}
        >

          <Box sx={{ px: 3 }}>

            <Tabs
  value={activeTabName}
  onChange={handleTabChange}
  variant="scrollable"
  scrollButtons="auto"
  allowScrollButtonsMobile
  TabIndicatorProps={{
    sx: {
      height: 3,
      borderRadius: 2,
      backgroundColor: "#0074c8"
    }
  }}
  sx={{
    minHeight: 44,
    "& .MuiTabs-flexContainer": {
      gap: 1
    },
    "& .MuiTab-root": {
      textTransform: "none",
      fontFamily: "proxima-nova, sans-serif",
      fontWeight: 600,
      fontSize: { xs: 14, sm: 16 },
      minHeight: 44,
      px: 2,
      borderRadius: 1,
      whiteSpace: "nowrap",
      color: "text.primary",

      "&:hover": {
        backgroundColor: "rgba(0,0,0,0.06)"
      },

      "&.Mui-selected": {
        color: "#0074c8"
      }
    }
  }}
>
  {EventTabs.map((t) => (
    <Tab
      key={t.id}
      value={t.name}
      label={t.label || t.name}
      wrapped={false}
    />
  ))}
</Tabs>

          </Box>

        </Box>


        {/* ------------------------------------------- */}
        {/* FORM */}
        {/* ------------------------------------------- */}

        <Box sx={{ p: 3 }}>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2
            }}
          >

         <form onSubmit={handleSubmit}>


              {/* ======================================= */}
              {/* SECTION 1 */}
              {/* ======================================= */}

              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Basic Event Information
                </h2>

                <div className={styles.sectionDescription}>
                  Define the core details about your event
                </div>


                <div className="row">

                   <div className="col-md-6">

                    <div className={styles.fieldGroup}>

                      <TextField

                        select

                        label="Event Type"

                        id="BP_Type"

                        name="BP_Type"

                        value={
                          bomEvent.BP_Type || ""
                        }

                        onChange={
                          handleTypeChange
                        }

                        fullWidth

                        size="small"

                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: 320
                              }
                            }
                          }
                        }}

                        helperText="Specify the event type"
                        InputLabelProps={{ shrink: true }}

                      >

                        <MenuItem value="">
                          (Please Select)
                        </MenuItem>

                        <MenuItem value="Project">
                          Project
                        </MenuItem>

                        <MenuItem value="Disaster Relief">
                          Disaster Relief
                        </MenuItem>

                        <MenuItem value="Camp">
                          Camp
                        </MenuItem>

                        <MenuItem value="Program">
                          Program
                        </MenuItem>

                        <MenuItem value="General">
                          General
                        </MenuItem>

                      </TextField>

                    </div>

                  </div>


                  <div className="col-md-12">

                    <div className={styles.fieldGroup}>

                      <TextField

                        label="Event Title"

                        required

                        id="BP_Title"

                        name="BP_Title"

                        inputProps={{
                          maxLength: 200
                        }}

                        fullWidth

                        size="small"

                        value={
                          bomEvent.BP_Title
                        }

                        onChange={
                          handleFieldChange
                        }

                        helperText="Event display title. Cannot contain any special characters."

                      />

                    </div>

                  </div>

                  <div className="col-md-12">

                    <div className={styles.fieldGroup}>

                      <TextField

                        label="Event Location"

                        id="BP_Address"

                        name="BP_Address"

                        fullWidth

                        size="small"

                        value={
                          bomEvent.BP_Address
                        }

                        onChange={
                          handleFieldChange
                        }

                        helperText="Provide the event location or address"
                        InputLabelProps={{ shrink: true }}

                      />

                    </div>

                  </div>


                  <div className="col-md-6">

                    <div className={styles.fieldGroup}>

                      <TextField

                        select

                        label="Partnership"

                        id="BP_Partnership"

                        name="BP_Partnership"

                        value={
                          bomEvent.BP_Partnership || ""
                        }

                        onChange={
                          handlePartnershipsChange
                        }

                        fullWidth

                        size="small"

                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: 320
                              }
                            }
                          }
                        }}

                        helperText="Select partnership if event is associated with one"
                        InputLabelProps={{ shrink: true }}

                      >

                        <MenuItem value="">
                          None
                        </MenuItem>

                        {partnershipsList?.map(
                          ({
                            ItemID,
                            Name
                          }) => (

                            <MenuItem
                              key={ItemID}
                              value={Name}
                            >
                              {Name}
                            </MenuItem>

                          )
                        )}

                      </TextField>

                    </div>

                  </div>


                  <div className="col-md-6">

                    <div className={styles.fieldGroup}>

                      <TextField

                        select

                        label="Accounting Code"

                        required

                        id="BP_AccountingCode"

                        name="BP_AccountingCode"

                        value={
                          bomEvent.BP_AccountingCode || ""
                        }

                        onChange={
                          handleAccountingCodesChange
                        }

                        fullWidth

                        size="small"

                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: 320
                              }
                            }
                          }
                        }}

                        helperText="Used for accounts receivable to apply money to this event"
                        InputLabelProps={{ shrink: true }}

                      >

                        <MenuItem value="">
                          Select Code
                        </MenuItem>

                        {accountingCodesList?.map(
                          ({
                            ItemID,
                            Name
                          }) => (

                            <MenuItem
                              key={ItemID}
                              value={Name}
                            >
                              {Name}
                            </MenuItem>

                          )
                        )}

                      </TextField>

                    </div>

                  </div>


                  <div className="col-md-12">

                    <div className={styles.fieldGroup}>

                      <TextField

                        select

                        label="Event Categories"

                        id="BP_Categories"

                        name="BP_Categories"

                        value={
                          bomEvent.BP_Categories || []
                        }

                        onChange={
                          handleCategoriesChange
                        }

                        fullWidth

                        size="small"

                        SelectProps={{

                          multiple: true,

                          renderValue: selected =>

                            categoriesList

                              .filter(c =>
                                (selected || [])
                                  .some(
                                    s =>
                                      Number(s) ===
                                      Number(
                                        c.CategoryID
                                      )
                                  )
                              )

                              .map(
                                c =>
                                  c.CategoryDisplayName
                              )

                              .join(", "),

                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: 320
                              }
                            }
                          }

                        }}

                        helperText="Select one or more categories to help attendees find this event"
                        InputLabelProps={{ shrink: true }}

                      >

                        {categoriesList?.map(
                          ({
                            CategoryDisplayName,
                            CategoryID
                          }) => (

                            <MenuItem
                              key={CategoryID}
                              value={CategoryID}
                            >

                              <Checkbox

                                size="small"

                                sx={{
                                  py: 0,
                                  mr: 1
                                }}

                                checked={
                                  (
                                    bomEvent.BP_Categories ||
                                    []
                                  ).some(
                                    s =>
                                      Number(s) ===
                                      Number(CategoryID)
                                  )
                                }

                              />

                              <ListItemText
                                primary={
                                  CategoryDisplayName
                                }
                              />

                            </MenuItem>

                          )
                        )}

                      </TextField>

                    </div>

                  </div>

                </div>

              </div>


              {/* ======================================= */}
              {/* SECTION 2 */}
              {/* ======================================= */}

              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Event Schedule
                </h2>

                <div className={styles.sectionDescription}>
                  Configure dates, times, and scheduling options
                </div>


                <div className="row">

                  <div className="col-md-12">

                    <div className={styles.toggleCard}>

                      <div className={styles.toggleHeader}>

                        <Switch

                          id="BP_ShowDateOnly"

                          checked={
                            bomEvent.BP_ShowDateOnly ||
                            false
                          }

                          onChange={
                            handleCheckboxChange
                          }

                          size="small"

                          sx={switchSx}

                        />

                        <label
                          htmlFor="BP_ShowDateOnly"
                          className={
                            styles.toggleLabel
                          }
                        >

                          <span
                            className={
                              styles.toggleTitle
                            }
                          >
                            Show Date Only (No Time)
                          </span>

                          <span
                            className={
                              styles.toggleDescription
                            }
                          >
                            Display only the date without specific times
                          </span>

                        </label>

                      </div>

                    </div>

                  </div>

                </div>


                <div className="row">

                  {bomEvent.BP_ShowDateOnly ? (
  <>
    <div className="col-md-6">
      <div className={styles.fieldGroup}>
        <LocalizationProvider dateAdapter={AdapterLuxonSunday}>
          <DatePicker
  label="Event Start Date *"
  value={bomEvent.BP_StartDate || null}
  onChange={handleStartDateChange}
  slotProps={{
    textField: {
      fullWidth: true,
      size: 'small',
      helperText: 'Select the event start date',
    },
  }}
/>
        </LocalizationProvider>
      </div>
    </div>

    <div className="col-md-6">
      <div className={styles.fieldGroup}>
        <LocalizationProvider dateAdapter={AdapterLuxonSunday}>
          <DatePicker
  label="Event End Date *"
  value={bomEvent.BP_EndDate || null}
  onChange={handleEndDateChange}
  slotProps={{
    textField: {
      fullWidth: true,
      size: 'small',
      helperText: 'Select the event end date',
    },
  }}
/>
        </LocalizationProvider>
      </div>
    </div>
  </>
) : (
  <>
    <div className="col-md-6">
      <div className={styles.fieldGroup}>
        <LocalizationProvider dateAdapter={AdapterLuxonSunday}>
          <DateTimePicker
  label="Event Start Date *"
  value={bomEvent.BP_StartDate || null}
  onChange={handleStartDateChange}
  slotProps={{
    textField: {
      fullWidth: true,
      size: 'small',
      helperText: 'Select the event start date',
    },
  }}
/>
        </LocalizationProvider>
      </div>
    </div>

    <div className="col-md-6">
      <div className={styles.fieldGroup}>
        <LocalizationProvider dateAdapter={AdapterLuxonSunday}>
          <DateTimePicker
  label="Event End Date *"
  value={bomEvent.BP_EndDate || null}
  onChange={handleEndDateChange}
  slotProps={{
    textField: {
      fullWidth: true,
      size: 'small',
      helperText: 'Select the event end date',
    },
  }}
/>
        </LocalizationProvider>
      </div>
    </div>
  </>
)}

                </div>


                <div className={styles.settingsGrid}>


                  <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="BP_ShowEndTime"
                        checked={
                          bomEvent.BP_ShowEndTime ||
                          false
                        }
                        onChange={
                          handleCheckboxChange
                        }
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="BP_ShowEndTime"
                        className={
                          styles.toggleLabel
                        }
                      >

                        <span
                          className={
                            styles.toggleTitle
                          }
                        >
                          Show End Date to Users
                        </span>

                        <span
                          className={
                            styles.toggleDescription
                          }
                        >
                          Display the end date on the public event page
                        </span>

                      </label>

                    </div>

                  </div>


                  <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="BP_DatesFlexible"
                        checked={
                          bomEvent.BP_DatesFlexible ||
                          false
                        }
                        onChange={
                          handleCheckboxChange
                        }
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="BP_DatesFlexible"
                        className={
                          styles.toggleLabel
                        }
                      >

                        <span
                          className={
                            styles.toggleTitle
                          }
                        >
                          Flexible Dates
                        </span>

                        <span
                          className={
                            styles.toggleDescription
                          }
                        >
                          Allow attendees to select their own dates
                        </span>

                      </label>

                    </div>

                  </div>

                </div>

              </div>


              {/* ======================================= */}
              {/* SECTION 3 */}
              {/* ======================================= */}

              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Event Restrictions
                </h2>

                <div className={styles.sectionDescription}>
                  Set limitations and requirements for attendees
                </div>


                <div className="row">


                  <div className="col-md-6">

                    <div className={styles.fieldGroup}>

                      <TextField

                        select

                        label="Overnight / Day Event"

                        id="BP_Overnight"

                        name="BP_Overnight"

                        value={
                          bomEvent.BP_Overnight ||
                          "(none)"
                        }

                        onChange={
                          handleOvernightChange
                        }

                        fullWidth

                        size="small"
                        InputLabelProps={{ shrink: true }}

                      >

                        <MenuItem value="(none)">
                          (none)
                        </MenuItem>

                        <MenuItem value="Overnight">
                          Overnight
                        </MenuItem>

                        <MenuItem value="Day">
                          Day Event
                        </MenuItem>

                      </TextField>

                    </div>

                  </div>


                  <div className="col-md-6">

                    <div className={styles.fieldGroup}>

                      <TextField

                        select

                        label="Gender Restrictions"

                        id="BP_Gender"

                        name="BP_Gender"

                        value={
                          bomEvent.BP_Gender ||
                          "(none)"
                        }

                        onChange={
                          handleGenderChange
                        }

                        fullWidth

                        size="small"
                        InputLabelProps={{ shrink: true }}

                      >

                        <MenuItem value="(none)">
                          (none)
                        </MenuItem>

                        <MenuItem value="No Restrictions">
                          No Restrictions
                        </MenuItem>

                        <MenuItem value="Male">
                          Male Only
                        </MenuItem>

                        <MenuItem value="Female">
                          Female Only
                        </MenuItem>

                      </TextField>

                    </div>

                  </div>


                  <div className="col-md-6">

                    <div className={styles.fieldGroup}>

                      <TextField

                        label="Grade Restrictions"

                        id="BP_AgeGradeRestrictions"

                        name="BP_AgeGradeRestrictions"

                        fullWidth

                        size="small"

                        value={
                          bomEvent.BP_AgeGradeRestrictions ||
                          ""
                        }

                        onChange={
                          handleFieldChange
                        }

                        helperText='Enter grade restrictions (e.g., "6th-12th grade")'
                        InputLabelProps={{ shrink: true }}

                      />

                    </div>

                  </div>


                  <div className="col-md-6">

                    <div className={styles.fieldGroup}>

                      <TextField

                        label="Age Restrictions"

                        id="BP_AgeGrade"

                        name="BP_AgeGrade"

                        fullWidth

                        size="small"

                        value={
                          bomEvent.BP_AgeGrade ||
                          ""
                        }

                        onChange={
                          handleFieldChange
                        }

                        helperText='Enter age restrictions (e.g., "12-18 years")'
                        InputLabelProps={{ shrink: true }}

                      />

                    </div>

                  </div>


                  <div className="col-md-6">

                    <div className={styles.fieldGroup}>

                      <TextField

                        select

                        label="Required Immunizations"

                        id="BP_RequiredShots"

                        name="BP_RequiredShots"

                        value={
                          bomEvent.BP_RequiredShots ||
                          []
                        }

                        onChange={
                          handleImmunizationsChange
                        }

                        fullWidth

                        size="small"

                        SelectProps={{
                          multiple: true,

                          renderValue: selected =>

                            immunizations
                              .filter(o =>
                                (selected || [])
                                  .some(
                                    s =>
                                      Number(s) ===
                                      Number(o.id)
                                  )
                              )
                              .map(o => o.label)
                              .join(", "),

                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: 320
                              }
                            }
                          }
                        }}

                        helperText="Specify if this event requires immunizations"
                        InputLabelProps={{ shrink: true }}

                      >

                        {immunizations?.map(
                          option => (

                            <MenuItem
                              key={option.id}
                              value={option.id}
                            >
                              <Checkbox
                                size="small"
                                sx={{ py: 0, mr: 1 }}
                                checked={
                                  (bomEvent.BP_RequiredShots || []).some(
                                    s => Number(s) === Number(option.id)
                                  )
                                }
                              />
                              <ListItemText primary={option.label} />
                            </MenuItem>

                          )
                        )}

                      </TextField>

                    </div>

                  </div>

                </div>

              </div>


              {/* ======================================= */}
              {/* SECTION 4 */}
              {/* ======================================= */}

              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Event Description
                </h2>

                <div className={styles.sectionDescription}>
                  Provide detailed information about the event
                </div>


                <Box>

                  <label
                    htmlFor="BP_Description"
                    style={{
                      display: "block",
                      marginBottom: 6,
                      fontWeight: 600,
                      fontSize: "0.875rem"
                    }}
                  >
                    Event Description
                  </label>

                  <JoditEditor

                    ref={editor}

                    id="BP_Description"

                    value={
                      bomEvent.BP_Description || ""
                    }

                    config={config}

                    onChange={
                      handleEditorChange
                    }

                  />

                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        "text.secondary",
                      display: "block",
                      mt: 0.5
                    }}
                  >
                    Enter a detailed description/overview for this event
                  </Typography>

                </Box>

              </div>


              {/* ======================================= */}
              {/* SECTION 5 */}
              {/* ======================================= */}

              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Event Management
                </h2>

                <div className={styles.sectionDescription}>
                  Configure event contacts and administration
                </div>


                <div className="row">

                  <div className="col-md-12">


                    {/* PRIVATE EVENT */}

                    <div className={styles.toggleCard}>

                      <div className={styles.toggleHeader}>

                        <Switch

                          id="BP_Private"

                          checked={
                            bomEvent.BP_Private ||
                            false
                          }

                          onChange={
                            handleCheckboxChange
                          }

                          size="small"

                          sx={switchSx}

                        />

                        <label
                          htmlFor="BP_Private"
                          className={
                            styles.toggleLabel
                          }
                        >

                          <span
                            className={
                              styles.toggleTitle
                            }
                          >
                            Private Event
                          </span>

                        </label>

                      </div>


                      <small>
                        If checked, the event won't show to users in the events list
                      </small>


                      {isEditMode &&
                        bomEvent.BP_Private && (

                          <p>

                            <Link
                              to={`https://app.baptistsonmission.org/events/details/${eventid}`}
                            >
                              https://app.baptistsonmission.org/events/details/{eventid}
                            </Link>

                          </p>

                        )}

                    </div>


                    {/* COORDINATOR / ADMIN */}

                    <div className={styles.toggleCard}>


                      <div>

                        <div className={styles.subsectionTitle}>
                          Event Coordinator
                        </div>


                        <div className="row">


                          <div className="col-md-6">

                            <div className={styles.fieldGroup}>

                              <TextField

                                select

                                label="Select Coordinator"

                                required

                                id="BP_Coordinator"

                                name="BP_Coordinator"

                                value={
                                  bomEvent.BP_Coordinator ||
                                  ""
                                }

                                onChange={
                                  handleCoordinatorChange
                                }

                                fullWidth

                                size="small"
                                InputLabelProps={{ shrink: true }}

                              >

                                <MenuItem value="">
                                  Select Coordinator
                                </MenuItem>

                                {eventCoordinators?.map(
                                  ({
                                    UserName,
                                    UserID
                                  }) => (

                                    <MenuItem
                                      key={UserID}
                                      value={UserID}
                                    >
                                      {UserName}
                                    </MenuItem>

                                  )
                                )}

                              </TextField>

                            </div>

                          </div>


                          <div className="col-md-6">

                            <div
                              className={
                                styles.contactOptionsGrid
                              }
                            >


                              <div
                                className={
                                  styles.toggleCard
                                }
                              >

                                <div
                                  className={
                                    styles.toggleHeader
                                  }
                                >

                                  <Switch
                                    id="BP_ShowCoordinatorEmail"
                                    checked={
                                      bomEvent.BP_ShowCoordinatorEmail ||
                                      false
                                    }
                                    onChange={
                                      handleCheckboxChange
                                    }
                                    size="small"
                                    sx={switchSx}
                                  />

                                  <label
                                    htmlFor="BP_ShowCoordinatorEmail"
                                    className={
                                      styles.toggleLabel
                                    }
                                  >

                                    <span
                                      className={
                                        styles.toggleTitle
                                      }
                                    >
                                      Show Email
                                    </span>

                                  </label>

                                </div>

                              </div>


                              <div
                                className={
                                  styles.toggleCard
                                }
                              >

                                <div
                                  className={
                                    styles.toggleHeader
                                  }
                                >

                                  <Switch
                                    id="BP_ShowCoordinatorPhone"
                                    checked={
                                      bomEvent.BP_ShowCoordinatorPhone ||
                                      false
                                    }
                                    onChange={
                                      handleCheckboxChange
                                    }
                                    size="small"
                                    sx={switchSx}
                                  />

                                  <label
                                    htmlFor="BP_ShowCoordinatorPhone"
                                    className={
                                      styles.toggleLabel
                                    }
                                  >

                                    <span
                                      className={
                                        styles.toggleTitle
                                      }
                                    >
                                      Show Phone
                                    </span>

                                  </label>

                                </div>

                              </div>


                              <div
                                className={
                                  styles.toggleCard
                                }
                              >

                                <div
                                  className={
                                    styles.toggleHeader
                                  }
                                >

                                  <Switch
                                    id="BP_SendCoordinatorEmail"
                                    checked={
                                      bomEvent.BP_SendCoordinatorEmail ||
                                      false
                                    }
                                    onChange={
                                      handleCheckboxChange
                                    }
                                    size="small"
                                    sx={switchSx}
                                  />

                                  <label
                                    htmlFor="BP_SendCoordinatorEmail"
                                    className={
                                      styles.toggleLabel
                                    }
                                  >

                                    <span
                                      className={
                                        styles.toggleTitle
                                      }
                                    >
                                      Send Notifications
                                    </span>

                                  </label>

                                </div>

                              </div>

                            </div>

                          </div>

                        </div>


                        <div className={styles.subsectionTitle}>
                          Event Administrator
                        </div>


                        <div className="row">


                          <div className="col-md-6">

                            <div className={styles.fieldGroup}>

                              <TextField

                                select

                                label="Select Administrator"

                                id="BP_Administrator"

                                name="BP_Administrator"

                                value={
                                  bomEvent.BP_Administrator ||
                                  ""
                                }

                                onChange={
                                  handleAdministratorChange
                                }

                                fullWidth

                                size="small"
                                InputLabelProps={{ shrink: true }}

                              >

                                <MenuItem value="">
                                  Select Administrator
                                </MenuItem>

                                {eventCoordinators?.map(
                                  ({
                                    UserName,
                                    UserID
                                  }) => (

                                    <MenuItem
                                      key={UserID}
                                      value={UserID}
                                    >
                                      {UserName}
                                    </MenuItem>

                                  )
                                )}

                              </TextField>

                            </div>

                          </div>


                          <div className="col-md-6">

                            <div
                              className={
                                styles.contactOptionsGrid
                              }
                            >


                              <div
                                className={
                                  styles.toggleCard
                                }
                              >

                                <div
                                  className={
                                    styles.toggleHeader
                                  }
                                >

                                  <Switch
                                    id="BP_ShowAdministratorEmail"
                                    checked={
                                      bomEvent.BP_ShowAdministratorEmail ||
                                      false
                                    }
                                    onChange={
                                      handleCheckboxChange
                                    }
                                    size="small"
                                    sx={switchSx}
                                  />

                                  <label
                                    htmlFor="BP_ShowAdministratorEmail"
                                    className={
                                      styles.toggleLabel
                                    }
                                  >

                                    <span
                                      className={
                                        styles.toggleTitle
                                      }
                                    >
                                      Show Email
                                    </span>

                                  </label>

                                </div>

                              </div>


                              <div
                                className={
                                  styles.toggleCard
                                }
                              >

                                <div
                                  className={
                                    styles.toggleHeader
                                  }
                                >

                                  <Switch
                                    id="BP_ShowAdministratorPhone"
                                    checked={
                                      bomEvent.BP_ShowAdministratorPhone ||
                                      false
                                    }
                                    onChange={
                                      handleCheckboxChange
                                    }
                                    size="small"
                                    sx={switchSx}
                                  />

                                  <label
                                    htmlFor="BP_ShowAdministratorPhone"
                                    className={
                                      styles.toggleLabel
                                    }
                                  >

                                    <span
                                      className={
                                        styles.toggleTitle
                                      }
                                    >
                                      Show Phone
                                    </span>

                                  </label>

                                </div>

                              </div>


                              <div
                                className={
                                  styles.toggleCard
                                }
                              >

                                <div
                                  className={
                                    styles.toggleHeader
                                  }
                                >

                                  <Switch
                                    id="BP_SendAdministratorEmail"
                                    checked={
                                      bomEvent.BP_SendAdministratorEmail ||
                                      false
                                    }
                                    onChange={
                                      handleCheckboxChange
                                    }
                                    size="small"
                                    sx={switchSx}
                                  />

                                  <label
                                    htmlFor="BP_SendAdministratorEmail"
                                    className={
                                      styles.toggleLabel
                                    }
                                  >

                                    <span
                                      className={
                                        styles.toggleTitle
                                      }
                                    >
                                      Send Notifications
                                    </span>

                                  </label>

                                </div>

                              </div>

                            </div>

                          </div>

                        </div>

                      </div>

                    </div>

                  </div>

                </div>

              </div>


              {/* ======================================= */}
              {/* ACTION BUTTONS */}
              {/* ======================================= */}

              <Box
                className={styles.actionButtons}
              >

                <Button

                  variant="contained"

                  type="submit"

                  disabled={isSaving}

                >

                  {isSaving
                    ? isEditMode
                      ? "Updating..."
                      : "Creating..."
                    : isEditMode
                      ? "Update Event"
                      : "Save Event"}

                </Button>


                <Button

                  variant="outlined"

                  type="button"

                  onClick={
                    handleCancel
                  }

                  disabled={isSaving}

                >

                  Cancel

                </Button>

              </Box>

            </form>

          </Paper>

        </Box>

      </div>

    </main>

  );

};


export default EventsForm;